/* ============================================================
   Fence Works — weather-gated scheduling (docs/SERVICE_SWITCHER_PLAN.md
   §13 item 13). Pure, import-free: the unit harness transpiles it alone.

   The forecast is lib/weather.ts's map — { "YYYY-MM-DD": { rain, lo, hi,
   dew } } for 16 days — and the GRADE is handed in (window.VFP_WEATHER.risk
   → lib/weatherrules.ts weatherRisk: rain and frost for a fence day; a dry
   slab, 40°F and the dew point for a coating; frozen ground for a lift).
   This file only answers the schedule's questions:
     • jobWeather   — the worst graded day across a job's span
     • nextGoodDay  — the first workday after it that the forecast grades
                      "ok" for the job's whole length, with the crew free
     • geoOf        — where the job is, whichever shape stored it
     • weatherFlagLive — is the server's overnight flag still worth showing
   Dates are LOCAL "YYYY-MM-DD" strings, like the rest of the schedule.
   ============================================================ */
export type WxLevel = "ok" | "med" | "high";
export type WxRisk = { level: WxLevel; label: string; why?: string } | null;
export type WxGrade = (day: any) => WxRisk;
export type JobWeather = { level: WxLevel; label: string; why: string; day: string; graded: number } | null;

export const DEFAULT_WORKDAYS = [1, 2, 3, 4, 5];
const RANK: Record<string, number> = { ok: 0, med: 1, high: 2 };
const pad = (n: number) => String(n).padStart(2, "0");

export function ymdOf(d: Date): string { return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()); }
export function parseYmdLocal(s: any): Date | null {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(s + "T00:00:00");
  return isNaN(d.getTime()) ? null : d;
}
export function addYmd(s: string, n: number): string {
  const d = parseYmdLocal(s);
  if (!d) return s;
  d.setDate(d.getDate() + (Number(n) || 0));
  return ymdOf(d);
}
export function dowOf(s: string): number { const d = parseYmdLocal(s); return d ? d.getDay() : -1; }
/* "Thu 23" — the chip's day */
export function dayLabel(s: string): string {
  const d = parseYmdLocal(s);
  return d ? d.toLocaleDateString("en-US", { weekday: "short" }) + " " + d.getDate() : String(s || "");
}
/* how many calendar days a job spans (≥ 1) */
export function spanOf(wo: any): number {
  const a = parseYmdLocal(wo && wo.scheduledStart), b = parseYmdLocal(wo && (wo.scheduledEnd || wo.scheduledStart));
  if (!a || !b) return 1;
  return Math.max(1, Math.round((b.getTime() - a.getTime()) / 86400000) + 1);
}

/* the worst graded day across [startYmd, endYmd] (at most `maxDays` of it) —
   null when the forecast covers none of them. `level: "ok"` comes back too,
   so a caller can tell "clear" from "unknown". */
export function jobWeather(fc: any, startYmd: string, endYmd: string | null | undefined, grade: WxGrade, opts: { maxDays?: number } = {}): JobWeather {
  if (!fc || typeof fc !== "object" || typeof grade !== "function") return null;
  const start = parseYmdLocal(startYmd);
  if (!start) return null;
  const n = Math.max(1, Math.min(opts.maxDays || 6, spanOf({ scheduledStart: startYmd, scheduledEnd: endYmd || startYmd })));
  let worst: JobWeather = null, graded = 0;
  for (let i = 0; i < n; i++) {
    const day = addYmd(startYmd, i);
    if (!fc[day]) continue;
    const r = grade(fc[day]);
    if (!r || !r.level) continue;
    graded++;
    if (!worst || RANK[r.level] > RANK[worst.level]) worst = { level: r.level, label: r.label || "", why: r.why || "", day, graded: 0 };
  }
  return worst ? { ...worst, graded } : null;
}

/* the first day after `fromYmd` that (a) the crew works, (b) the forecast
   grades "ok" for every day of the job's span, (c) the crew is free on —
   within the forecast's horizon. null when nothing in the window qualifies
   (the owner picks by hand). */
export function nextGoodDay(fc: any, fromYmd: string, grade: WxGrade, opts: { workdays?: number[]; span?: number; maxDays?: number; busy?: (ymd: string) => boolean } = {}): string | null {
  if (!fc || typeof fc !== "object" || typeof grade !== "function" || !parseYmdLocal(fromYmd)) return null;
  const workdays = Array.isArray(opts.workdays) && opts.workdays.length ? opts.workdays : DEFAULT_WORKDAYS;
  const span = Math.max(1, Math.min(14, Number(opts.span) || 1));
  const maxDays = Math.max(1, Math.min(31, Number(opts.maxDays) || 16));
  for (let i = 1; i <= maxDays; i++) {
    const day = addYmd(fromYmd, i);
    if (!workdays.includes(dowOf(day))) continue;
    if (opts.busy && opts.busy(day)) continue;
    let ok = true;
    for (let k = 0; k < span; k++) {
      const d = addYmd(day, k);
      if (!workdays.includes(dowOf(d)) || !fc[d]) { ok = false; break; }
      const r = grade(fc[d]);
      if (!r || r.level !== "ok") { ok = false; break; }
    }
    if (ok) return day;
  }
  return null;
}

/* where the job is: wo.geo as { lat, lng } (the office's geocode) or
   [lat, lng] (a crew pin), else the quote's drawn site / parcel centre */
export function geoOf(wo: any, quote?: any): { lat: number; lng: number } | null {
  const pt = (p: any): { lat: number; lng: number } | null => {
    if (!p) return null;
    const lat = Array.isArray(p) ? +p[0] : +(p.lat != null ? p.lat : p.latitude);
    const lng = Array.isArray(p) ? +p[1] : +(p.lng != null ? p.lng : p.lon != null ? p.lon : p.longitude);
    if (!isFinite(lat) || !isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
    return { lat, lng };
  };
  const own = pt(wo && wo.geo);
  if (own) return own;
  const c = quote && quote.inputs ? quote.inputs.concrete : null;
  const site = c && c.site ? pt(c.site.center) : null;
  if (site) return site;
  const par = quote && quote.inputs ? quote.inputs.parcel : null;
  return par ? pt(par.center) || pt(par.queryPoint) : null;
}

/* the server's overnight flag (data.weatherFlag — scheduler.js tickForecast
   → automations.js weather): shown while its day hasn't passed */
export function weatherFlagLive(flag: any, todayYmd: string): boolean {
  return !!(flag && typeof flag === "object" && typeof flag.day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(flag.day) && flag.day >= todayYmd);
}

/* is this crew already on another job that day (same lane, overlapping span)? */
export function crewBusyOn(orders: any[], crewId: any, ymd: string, exceptId?: any): boolean {
  if (!Array.isArray(orders) || !ymd) return false;
  return orders.some((o: any) => o && o.id !== exceptId && (o.crewId || "") === (crewId || "") && o.scheduledStart && o.status !== "completed"
    && o.scheduledStart <= ymd && (o.scheduledEnd || o.scheduledStart) >= ymd);
}
